import React, { FC } from 'react';
import PropTypes from 'prop-types';
import { CardFooter, CardFooterLeft, CardFooterRight } from './bootstrap/Card';
import Pagination, { PaginationItem } from './bootstrap/Pagination';
import Select from './bootstrap/forms/Select';
import Option from './bootstrap/Option';

export const PER_COUNT = {
	1:1,
	20: 20,
	30: 30,
	50: 50,
	100 : 100
};

export const dataPagination = (data: any[], currentPage: number, perPage: number) =>
	data.filter(
		(i, index) => index + 1 > (currentPage - 1) * perPage && index + 1 <= currentPage * perPage,
	);

interface IPaginationButtonsProps {
	// New API
	setCurrentPage?(...args: unknown[]): unknown;
	currentPage?: number;
	perPage?: number;
	setPerPage?(...args: unknown[]): unknown;
	count?: number;
	label?: string;
	// Old API (for Topics.js and Lessons.js)
	totalPages?: number;
	onPageChange?(...args: unknown[]): unknown;
	totalCount?: number;
	limit?: number;
}
const PaginationButtons: FC<IPaginationButtonsProps> = ({
	setCurrentPage,
	currentPage,
	perPage,
	setPerPage,
	count,
	label,
	// Old API
	totalPages,
	onPageChange,
	totalCount,
	limit,
}) => {
	// Support both APIs
	const isOldAPI = totalPages !== undefined || onPageChange !== undefined;
	
	// Default values to prevent undefined errors
	const safePerPage = isOldAPI ? (limit || 20) : (perPage || 20);
	const safeCurrentPage = currentPage || 1;
	const safeCount = isOldAPI ? (totalCount || 0) : (count || 0);
	const safeTotalPages = isOldAPI ? (totalPages || 1) : Math.ceil(safeCount / safePerPage);
	
	const totalItems = safeCount;
	const totalPage = safeTotalPages;
	
	// Handle page change for both APIs
	const handlePageChange = (newPage: number) => {
		if (isOldAPI && onPageChange) {
			onPageChange(newPage);
		} else if (setCurrentPage) {
			setCurrentPage(newPage);
		}
	};
	
	// Handle per page change
	const handlePerPageChange = (newPerPage: number) => {
		if (setPerPage) {
			setPerPage(newPerPage);
			if (setCurrentPage) {
				setCurrentPage(1);
			}
		}
	};

	const pagination = () => {
		let items = [];

		let i = safeCurrentPage - 1;
		while (i >= safeCurrentPage - 1 && i > 0) {
			items.push(
				<PaginationItem key={i} onClick={() => handlePageChange(safeCurrentPage - 1)}>
					{i}
				</PaginationItem>,
			);

			i -= 1;
		}

		items = items.reverse();

		items.push(
			<PaginationItem key={safeCurrentPage} isActive onClick={() => handlePageChange(safeCurrentPage)}>
				{safeCurrentPage}
			</PaginationItem>,
		);

		i = safeCurrentPage + 1;
		while (i <= safeCurrentPage + 1 && i <= totalPage) {
			items.push(
				<PaginationItem key={i} onClick={() => handlePageChange(safeCurrentPage + 1)}>
					{i}
				</PaginationItem>,
			);

			i += 1;
		}

		return items;
	};

	const getInfo = () => {
		const start = safePerPage * (safeCurrentPage - 1) + 1;

		const end = safePerPage * safeCurrentPage;

		return (
			<span className='pagination__desc' style={{marginRight : 20}}>
				{start} - {end > totalItems ? totalItems : end} / {totalItems} {" "}
			</span>
		);
	};

	return (
		<CardFooter>
			<CardFooterLeft>
				<span className='text-muted'>{getInfo()}</span>
			</CardFooterLeft>

			<CardFooterRight className='d-flex'>
				{totalPage > 1 && (
					// @ts-ignore
					<Pagination ariaLabel={label}>
						<PaginationItem
							isFirst
							isDisabled={!(safeCurrentPage - 1 > 0)}
							onClick={() => handlePageChange(1)}
						/>
						<PaginationItem
							isPrev
							isDisabled={!(safeCurrentPage - 1 > 0)}
							onClick={() => handlePageChange(safeCurrentPage - 1)}
						/>
						{safeCurrentPage - 1 > 1 && (
							<PaginationItem onClick={() => handlePageChange(safeCurrentPage - 2)}>
								...
							</PaginationItem>
						)}
						{pagination()}
						{safeCurrentPage + 1 < totalPage && (
							<PaginationItem onClick={() => handlePageChange(safeCurrentPage + 2)}>
								...
							</PaginationItem>
						)}
						<PaginationItem
							isNext
							isDisabled={!(safeCurrentPage + 1 <= totalPage)}
							onClick={() => handlePageChange(safeCurrentPage + 1)}
						/>
						<PaginationItem
							isLast
							isDisabled={!(safeCurrentPage + 1 <= totalPage)}
							onClick={() => handlePageChange(totalPage)}
						/>
					</Pagination>
				)}

				{setPerPage && (
				<Select
					size='sm'
					ariaLabel='Per'
					onChange={(e: { target: { value: string } }) => {
							handlePerPageChange(parseInt(e.target.value, 10));
					}}
						value={safePerPage.toString()}>
					{Object.keys(PER_COUNT).map((i) => (
						<Option key={i} value={i}>
							{i}
						</Option>
					))}
				</Select>
				)}
			</CardFooterRight>
		</CardFooter>
	);
};
PaginationButtons.propTypes = {
	// New API
	setCurrentPage: PropTypes.func,
	currentPage: PropTypes.number,
	perPage: PropTypes.number,
	setPerPage: PropTypes.func,
	count: PropTypes.number,
	label: PropTypes.string,
	// Old API
	totalPages: PropTypes.number,
	onPageChange: PropTypes.func,
	totalCount: PropTypes.number,
	limit: PropTypes.number,
};
PaginationButtons.defaultProps = {
	label: 'items',
};

export default PaginationButtons;
